import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./edit-attachments-modal.module.scss";
import Button from "../../components/ui-kit/button/button";
import DocumentDeleteItem from "../../components/ui-kit/document-delete/document-delete";
import {
  changeAttachmentsLayer,
  deleteAttachments,
  getAttachmentDownload,
} from "../../services/api";
import { Attachment } from "../../interfaces/feature-info";
import {
  CardType,
  InvestmentOpportunity,
  LandPlot,
  SpaceForRent,
} from "../../interfaces/card";
import { saveAttachments } from "../../services/api";
import { firstSymbolsToUppercase } from "../../utils/first-symbols-to-uppercase";
import { renderStatusTag, renderTypeOfUseTag } from "../../utils/render-tags";
import ToggleSwitch from "../../components/ui-kit/toggle-switch/toggle-switch";
import ImageSlider from "../slider/slider";
import CustomScrollbar from "../../components/ui-kit/scrollbar/scrollbar";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { changeDetailsObjectFromArcgisAsync } from "../../services/actions/map";
import Input from "../../components/ui-kit/Input/input";
import Tooltip from "../../components/ui-kit/tooltip/tooltip";
import krookieIcon from "../../images/krookie-ico.svg";
import { v4 as uuidv4 } from "uuid";
import { Roles } from "../../types/roles";
import Modal from "../modal/modal";
import FileUploadButton from "./file-upload-button/file-upload-button";
import { isRTL } from "../../utils/rtl";

interface FileWithId {
  id: string;
  file: File;
}

interface Props {
  text: string;
  onClose: () => void;
  documents?: Attachment[];
  type: CardType;
  card: InvestmentOpportunity | LandPlot | SpaceForRent;
  images?: Attachment[];
  floor?: string | false;
  floorPlanAttachment: Attachment | null;
  layerId: string;
  featureId: string;
  onSaved: (newType: CardType, fid: string, didChangeType: boolean) => void;
}

const EditAttachmentsModal = ({
  onClose,
  text,
  documents = [],
  images,
  card,
  type,
  floor,
  floorPlanAttachment,
  layerId,
  featureId,
  onSaved,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [addToOpportunities, setAddToOpportunities] = useState<boolean>(
    "is_inv_op" in card ? card.is_inv_op === 1 : false,
  );
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<number[]>([]);
  const { description_a = "", description_e = "" } = card as {
    description_a?: string;
    description_e?: string;
  };
  const [arabicDescription, setArabicDescription] = useState<string>(
    description_a || "",
  );
  const [englishDescription, setEnglishDescription] = useState<string>(
    description_e || "",
  );
  const [newImages, setNewImages] = useState<FileWithId[]>([]);
  const [newDocuments, setNewDocuments] = useState<FileWithId[]>([]);
  const [newFloorPlan, setNewFloorPlan] = useState<FileWithId | null>(null);

  const isAttachmentManager = useAppSelector(
    (store) =>
      store.user.user?.roles.includes(Roles.AttachmentManager) ?? false,
  );
  const arabicInputRef = useRef<HTMLInputElement>(null);
  const englishInputRef = useRef<HTMLInputElement>(null);

  const updatedAttributes = useMemo<Record<string, any>>(() => {
    const upd: Record<string, any> = {};

    if (
      "description_a" in card &&
      arabicDescription !== (card.description_a ?? "")
    )
      upd.description_a = arabicDescription || null;

    if (
      "description_e" in card &&
      englishDescription !== (card.description_e ?? "")
    )
      upd.description_e = englishDescription || null;

    if ("is_inv_op" in card && (card.is_inv_op === 1) !== addToOpportunities)
      upd.is_inv_op = addToOpportunities ? 1 : 0;

    return upd;
  }, [arabicDescription, englishDescription, addToOpportunities, card]);

  const attachmentChanges = useMemo(
    () =>
      newImages.length > 0 ||
      newDocuments.length > 0 ||
      !!newFloorPlan ||
      attachmentsToDelete.length > 0,
    [newImages, newDocuments, newFloorPlan, attachmentsToDelete],
  );

  const hasChanges =
    attachmentChanges || Object.keys(updatedAttributes).length > 0;

  const handleClickDownloadButton = async (id: number, filename: string) => {
    try {
      const blob = await getAttachmentDownload(id, filename);
      const url = URL.createObjectURL(blob);

      window.open(url, "_blank");

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleClickDeleteButton = (id: number) => {
    setAttachmentsToDelete((prev) => [...prev, id]);
  };

  const handleClickDeleteUploadedImg = (id: string) => {
    setNewImages((prev) => prev.filter((item) => item.id !== id));
  };

  const handleFileChange = (
    files: File[],
    type: "photo" | "document" | "floorPlan",
  ) => {
    if (files && files.length > 0) {
      const newFiles = Array.from(files).map((file) => ({
        file,
        id: uuidv4(),
      }));
      if (type === "photo") {
        setNewImages((prev) => [...prev, ...newFiles]);
      } else if (type === "document") {
        setNewDocuments((prev) => [...prev, ...newFiles]);
      } else if (type === "floorPlan") {
        setNewFloorPlan(newFiles[0]);
      }
    }
  };

  const handleClickSave = async () => {
    try {
      const attachments: {
        files: File[];
        type: "image" | "document" | "floorPlan";
      }[] = [
        { files: newImages.map((image) => image.file), type: "image" },
        {
          files: newDocuments.map((document) => document.file),
          type: "document",
        },
        { files: newFloorPlan ? [newFloorPlan.file] : [], type: "floorPlan" },
      ];

      const hasIsInvOpChanged = updatedAttributes.hasOwnProperty("is_inv_op");

      await Promise.all(
        attachments
          .filter((item) => item.files.length)
          .map(async (item) => {
            await saveAttachments(item.files, item.type, layerId, featureId);
          }),
      );

      if (attachmentsToDelete.length > 0) {
        await deleteAttachments(attachmentsToDelete);
      }

      if (Object.keys(updatedAttributes).length) {
        await dispatch(
          changeDetailsObjectFromArcgisAsync({
            layer: card.layer,
            objectId: card.objectId,
            newAttributes: updatedAttributes,
          }),
        );
      }

      if (hasIsInvOpChanged) {
        await changeAttachmentsLayer(layerId, featureId);
      }

      const newType = hasIsInvOpChanged
        ? updatedAttributes.is_inv_op === 1
          ? "InvestmentOpportunities"
          : "Plots"
        : type;

      onSaved(newType, card.fid.toString(), hasIsInvOpChanged);
    } catch (error) {
      console.error(error);
    }
  };

  const scrollInputToEnd = (input: HTMLInputElement) => {
    const dir = isRTL() ? "rtl" : "ltr";
    const { scrollWidth, clientWidth } = input;
    const maxScroll = scrollWidth - clientWidth;

    if (dir === "ltr") {
      input.scrollLeft = scrollWidth;
      return;
    }

    input.scrollLeft = maxScroll;
    if (input.scrollLeft > 0) {
      return;
    }
    input.scrollLeft = -maxScroll;
  };

  const focusDescEnd = (ref: React.RefObject<HTMLInputElement>) => {
    setTimeout(() => {
      const input = ref.current;
      if (input) {
        const len = input.value.length;
        input.setSelectionRange(len, len);
        scrollInputToEnd(input);
      }
    }, 0);
  };

  const renderLabelContent = () => {
    switch (type) {
      case "Plots":
        return firstSymbolsToUppercase(text);
      case "InvestmentOpportunities":
        return renderEnglishDescription();
      case "SpacesForRent":
        return t("edit-attachments-modal.officeNumber", {
          number: card.number,
        });
      default:
        return null;
    }
  };

  const shouldShowEditableDescriptions =
    isAttachmentManager && type === "InvestmentOpportunities";

  const renderEnglishDescription = () => {
    if (type !== "InvestmentOpportunities") return null;

    return shouldShowEditableDescriptions ? (
      <div className={styles.editableInputWrapper}>
        <Input
          classes={{
            input: styles.editableInputDescEnglish,
            container: styles.editableInputContainer,
          }}
          value={englishDescription}
          onChange={setEnglishDescription}
          onFocus={() => focusDescEnd(englishInputRef)}
          ref={englishInputRef}
          placeholder={t("edit-attachments-modal.noData")}
        />
        <Tooltip
          classes={{ image: styles.tooltipImage }}
          text={t("edit-attachments-modal.tooltips.enterEnglish")}
        />
      </div>
    ) : (
      <span>{englishDescription || t("edit-attachments-modal.noData")}</span>
    );
  };

  const renderArabicDescription = () => {
    if (type !== "InvestmentOpportunities") return null;

    return shouldShowEditableDescriptions ? (
      <div
        style={{ paddingTop: "8px" }}
        className={styles.editableInputWrapper}
      >
        <Input
          classes={{
            input: styles.editableInputDescArabic,
            container: styles.editableInputContainer,
          }}
          value={arabicDescription}
          onChange={setArabicDescription}
          onFocus={() => focusDescEnd(arabicInputRef)}
          ref={arabicInputRef}
          placeholder={t("edit-attachments-modal.noData")}
        />
        <Tooltip
          classes={{ image: styles.tooltipImage }}
          text={t("edit-attachments-modal.tooltips.enterArabic")}
        />
      </div>
    ) : (
      <p className={styles.descA}>
        {arabicDescription || t("edit-attachments-modal.noData")}
      </p>
    );
  };

  const isFloorPlanUploadDisabled =
    (!!floorPlanAttachment &&
      !attachmentsToDelete.includes(floorPlanAttachment.id)) ||
    !!newFloorPlan ||
    !isAttachmentManager;

  return (
    <Modal
      className={styles.modal}
      closeOnOverlayClick={false}
      onClose={onClose}
      closeClass={styles.closeBtn}
    >
      <div className={styles.container}>
        <div className={styles.title}>
          <span className={styles.label}>{renderLabelContent()}</span>
          <div className={styles.tags}>
            {type !== "InvestmentOpportunities" &&
              renderStatusTag(card.status, styles.status)}
            {renderTypeOfUseTag(card.typeOfUse, styles.type)}
          </div>
        </div>

        {renderArabicDescription()}

        {type !== "SpacesForRent" && (
          <div className={styles.opportunityToggleItem}>
            <div className={styles.switch}>
              <ToggleSwitch
                isChecked={addToOpportunities}
                onChange={() => setAddToOpportunities((prev) => !prev)}
                disabled={!isAttachmentManager}
              />
              {type === "InvestmentOpportunities"
                ? t("edit-attachments-modal.toggle.removeFromOpportunities", {
                    description:
                      ("description_e" in card && card.description_e) ||
                      t("edit-attachments-modal.noData"),
                  })
                : t("edit-attachments-modal.toggle.addToOpportunities", {
                    plotName: firstSymbolsToUppercase(text),
                  })}
            </div>
            <Button
              type="outline"
              disabled={true}
              className={styles.viewKrookie}
            >
              <span className={styles.textContent}>
                <img
                  src={krookieIcon}
                  alt={t("edit-attachments-modal.krookieAlt")}
                />
                {t("edit-attachments-modal.viewKrookie")}
              </span>
            </Button>
          </div>
        )}

        <div
          className={`${styles.photos}${type === "SpacesForRent" ? ` ${styles.photosForRent}` : ""}`}
        >
          <div className={styles.title}>
            <span>{t("edit-attachments-modal.photos.header")}</span>
            <FileUploadButton
              buttonLabel={t("edit-attachments-modal.photos.addButton")}
              disabled={!isAttachmentManager}
              multiple={true}
              accept="image/jpeg"
              onChange={(files) => handleFileChange(files, "photo")}
              buttonClassName={styles.addPhotoBtn}
            />
          </div>
          <div className={styles.sliderWrapper}>
            <ImageSlider
              images={images ? images : []}
              uploadedImages={newImages}
              onDeleteImg={handleClickDeleteButton}
              onDeleteUploadedImg={handleClickDeleteUploadedImg}
              attachmentsToDelete={attachmentsToDelete}
              showDeleteButton={isAttachmentManager}
            />
          </div>
        </div>

        <div className={`${styles.information} ${styles[type]}`}>
          <span className={styles.title}>
            {t("edit-attachments-modal.information.header")}
          </span>
          <div className={styles.infoFields}>
            <span className={styles.cityInfo}>
              {t("edit-attachments-modal.information.city", {
                city: card.city,
              })}
            </span>
            <span className={styles.rentInfo}>
              {t("edit-attachments-modal.information.estimatedRent")}
            </span>
            {type === "SpacesForRent" && floor && (
              <span className={styles.floor}>
                {t("edit-attachments-modal.information.floor", { floor })}
              </span>
            )}
            {type === "InvestmentOpportunities" && (
              <span className={styles.plotName}>
                {firstSymbolsToUppercase(text)}
              </span>
            )}
            <span className={styles.areaInfo}>
              {type === "SpacesForRent"
                ? t("edit-attachments-modal.information.space")
                : t("edit-attachments-modal.information.plot")}{" "}
              {t("edit-attachments-modal.information.area", {
                area: card.area,
              })}
            </span>
          </div>
        </div>

        <div className={styles.attachmentsBlock}>
          <div className={styles.title}>
            <span>{t("edit-attachments-modal.attachments.header")}</span>
            <FileUploadButton
              buttonLabel={t("edit-attachments-modal.attachments.addButton")}
              disabled={!isAttachmentManager}
              multiple={true}
              accept="application/pdf"
              onChange={(files) => handleFileChange(files, "document")}
              buttonClassName={styles.addNewDocumentBtn}
            />
          </div>
          {(documents.length > 0 || newDocuments.length > 0) && (
            <CustomScrollbar
              height={
                documents.length * 56 + newDocuments.length * 56 > 164
                  ? "164px"
                  : documents.length * 56 + newDocuments.length * 56
              }
            >
              <div className={styles.attachmentsList}>
                {documents &&
                  documents.length > 0 &&
                  documents.map((document, index) => (
                    <DocumentDeleteItem
                      key={index}
                      filename={document.fileName}
                      onDownload={() =>
                        handleClickDownloadButton(
                          document.id,
                          document.fileName,
                        )
                      }
                      onDelete={() => handleClickDeleteButton(document.id)}
                      showDeleteButton={isAttachmentManager}
                      documentsToDelete={attachmentsToDelete}
                      id={document.id}
                    />
                  ))}
                {newDocuments &&
                  newDocuments.length > 0 &&
                  newDocuments.map((document) => (
                    <DocumentDeleteItem
                      key={document.id}
                      filename={document.file.name}
                      showDeleteButton={isAttachmentManager}
                      documentsToDelete={attachmentsToDelete}
                      deleteNew={() => {
                        setNewDocuments((prev) => {
                          return prev.filter((item) => item.id !== document.id);
                        });
                      }}
                    />
                  ))}
              </div>
            </CustomScrollbar>
          )}
        </div>

        {type === "SpacesForRent" && (
          <div className={styles.attachmentsBlock}>
            <div className={styles.title}>
              <span>{t("edit-attachments-modal.floorPlan.header")}</span>
              <FileUploadButton
                buttonLabel={t("edit-attachments-modal.floorPlan.addButton")}
                disabled={isFloorPlanUploadDisabled}
                multiple={false}
                accept="image/jpeg"
                onChange={(files) => handleFileChange(files, "floorPlan")}
                buttonClassName={styles.addNewDocumentBtn}
              />
            </div>
            {(floorPlanAttachment || newFloorPlan) && (
              <>
                {floorPlanAttachment && (
                  <DocumentDeleteItem
                    filename={
                      floorPlanAttachment.fileName.length > 20
                        ? `${floorPlanAttachment.fileName.slice(0, 20)}...`
                        : floorPlanAttachment.fileName
                    }
                    onDownload={() =>
                      handleClickDownloadButton(
                        floorPlanAttachment.id,
                        floorPlanAttachment.fileName,
                      )
                    }
                    onDelete={() =>
                      handleClickDeleteButton(floorPlanAttachment.id)
                    }
                    showDeleteButton={isAttachmentManager}
                    documentsToDelete={attachmentsToDelete}
                    id={floorPlanAttachment.id}
                  />
                )}
                {newFloorPlan && (
                  <DocumentDeleteItem
                    filename={
                      newFloorPlan.file.name.length > 20
                        ? `${newFloorPlan.file.name.slice(0, 20)}...`
                        : newFloorPlan.file.name
                    }
                    showDeleteButton={isAttachmentManager}
                    documentsToDelete={attachmentsToDelete}
                    deleteNew={() => {
                      setNewFloorPlan(null);
                    }}
                  />
                )}
              </>
            )}
          </div>
        )}

        <div className={styles.buttons}>
          <Button
            className={styles.saveButton}
            type="primary"
            onClick={() => handleClickSave()}
            disabled={!isAttachmentManager || !hasChanges}
          >
            {t("edit-attachments-modal.buttons.save")}
          </Button>
          <Button
            className={styles.outlineCloseButton}
            type="outline"
            onClick={onClose}
          >
            {t("edit-attachments-modal.buttons.close")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditAttachmentsModal;
